// Cross-tab recovery coordination for the recording-recovery subsystem. Two
// viewer-scoped localStorage maps:
//   - lock:   { recordingId: claimedAt } — at-most-one-tab guard with a TTL so a
//             crashed holder self-releases (the bare dismiss-array shape can't do
//             this, spec §14A). The drain, Path A, Path B, and the future
//             local-recordings tab all claim THIS one lock before re-parsing a
//             recording, so an M7 race collapses to one uploader (spec §16.2).
//   - giveUp: { recordingId: count } — how many times recovery has failed for a
//             recording; once it reaches MAX_RECOVERY_GIVEUP we stop re-pulling it
//             (bounds a PHI re-pull storm + a permanent signed-encounter reject).
//
// Mirrors recordingHeartbeat.js / recordingRecoveryDismissState.js: viewer-scoped
// key, recency-capped to MAX_IDS, silent on any storage failure (best-effort).
import { recordingViewerKey } from "./recordingViewerKey";

const LOCK_PREFIX = "recordingRecoveryLock:";
const GIVEUP_PREFIX = "recordingRecoveryGiveUp:";
const MAX_IDS = 50;
export const RECOVERY_LOCK_TTL_MS = 5 * 60 * 1000;
export const MAX_RECOVERY_GIVEUP = 3;

function lockKey() {
  return LOCK_PREFIX + recordingViewerKey();
}
function giveUpKey() {
  return GIVEUP_PREFIX + recordingViewerKey();
}

function readMap(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const p = JSON.parse(raw);
    return p && typeof p === "object" ? p : {};
  } catch {
    return {};
  }
}
function writeMap(key, map) {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_IDS) {
      // Drop the lowest-valued entries: oldest claimedAt for the lock map; least-
      // failed ids for the give-up map (keep the given-up ones, shed the noise).
      keys.sort((a, b) => (map[a] || 0) - (map[b] || 0));
      for (let i = 0; i < keys.length - MAX_IDS; i++) delete map[keys[i]];
    }
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // Quota / private-mode failures are silent — coordination is best-effort.
  }
}

// Claim the cross-tab recovery lock for one recording. Granted when there is no
// live holder OR the existing holder's claim is older than ttlMs (steal — a tab
// that crashed mid-recovery never released it). Stamps claimedAt on success.
export function claimRecoveryLock(recordingId, ttlMs) {
  if (!recordingId) return false;
  const map = readMap(lockKey());
  const claimedAt = map[recordingId];
  const now = Date.now();
  if (claimedAt != null && now - claimedAt <= ttlMs) return false; // a live holder owns it
  map[recordingId] = now;
  writeMap(lockKey(), map);
  return true;
}
export function releaseRecoveryLock(recordingId) {
  if (!recordingId) return;
  const map = readMap(lockKey());
  if (map[recordingId] != null) {
    delete map[recordingId];
    writeMap(lockKey(), map);
  }
}

export function bumpGiveUp(recordingId) {
  if (!recordingId) return 0;
  const map = readMap(giveUpKey());
  const next = (map[recordingId] || 0) + 1;
  map[recordingId] = next;
  writeMap(giveUpKey(), map);
  return next;
}
export function getGiveUp(recordingId) {
  if (!recordingId) return 0;
  return readMap(giveUpKey())[recordingId] || 0;
}

// Durably mark a recording as permanently given up (count = cap) so it is NEVER
// re-pulled — used when recovery hits an UNrecoverable error (the encounter was
// signed/archived). Unlike a single bumpGiveUp this survives a reload, so a signed
// encounter doesn't get re-attempted once per session.
export function markGivenUp(recordingId) {
  if (!recordingId) return;
  const map = readMap(giveUpKey());
  map[recordingId] = MAX_RECOVERY_GIVEUP;
  writeMap(giveUpKey(), map);
}

// Reset the give-up counter for a recording — an EXPLICIT user "Retry" must override
// the automated give-up so an abandoned recording isn't permanently stuck with no UI
// path back (the give-up map otherwise keeps maxed entries and never ages them out).
export function clearGiveUp(recordingId) {
  if (!recordingId) return;
  const map = readMap(giveUpKey());
  if (map[recordingId] != null) {
    delete map[recordingId];
    writeMap(giveUpKey(), map);
  }
}
